/* eslint-disable max-len */
import { message } from 'antd';
import dayjs from 'dayjs';
import React, {
  createContext, useEffect, useMemo, useState
} from 'react';
import { useQuery } from 'react-query';
import {
  Navigate, Outlet, useNavigate, useSearchParams
} from 'react-router-dom';

import {
  menuInsideChatBot, menuSidebar, menuSidebarInactive, menuSidebarExpried
} from 'assets/dummy/sidebar';
import Loading from 'components/atoms/Loading';
import Header from 'components/organisms/Header';
import Sidebar, { MenuItems } from 'components/organisms/Sidebar';
import useNetwork from 'hooks/useNetwork';
import useWindowDimensions from 'hooks/useWindowDemensions';
import { getAgentByIdService } from 'services/agents';
import { getAccessToken } from 'services/common/storage';
import { getOverViewService } from 'services/overview';
import { OverViewData } from 'services/overview/types';
import { getProfileAction } from 'store/auth';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getSystemAction } from 'store/system';
import { ROUTE_PATHS } from 'utils/constants';
import mapModifiers from 'utils/functions';

interface MainLayoutProps {
  children?: React.ReactNode;
}

type ContextProps = {
  getOverview: () => void;
} & OverViewData;

/* Contex */
export const BillingContex = createContext<ContextProps>({
  getOverview: () => { },
  planData: {
    code: '',
    startDate: '',
    endDate: '',
    billingType: 0,
    status: 0,
    orderCode: '',
    packageQty: 1,
    packageData: {
      name: '',
      duration: 0,
      price: 0,
      id: 0
    },
    addOnData: [{
      servicePackageAddOnData: {
        name: '',
        duration: 0,
      },
      quantity: 0,
      price: 0,
      totalPrice: 0,
      endAt: null,
      startAt: null,
      status: 0
    }],
    createdAt: '',
    updatedAt: '',
    isTrial: false
  },
  invoiceData: {
    invoiceType: 0,
    taxCode: '',
    companyName: '',
    fullAddress: '',
    invoiceEmails: [],
    nationalId: '',
    fullName: '',
  },
  requireDelInfo: {
    code: '',
    reqDelData: {
      agentCodes: [],
      documentCodes: []
    },
    documentData: [],
    endDate: '',
    packageData: {
      name: '',
      code: '',
      duration: 0,
      numberOfAgent: 0,
      numberOfFile: 0,
      totalSizeLimit: 0,
      numberOfWebsite: 0,
      numberOfSession: 0
    },
    startDate: '',
    status: 0
  }
});

const COLLAPSE_BREAKPOINT = 1024;

const MainLayout: React.FC<MainLayoutProps> = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = React.useState(true);
  const networkStatus = useNetwork();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { width } = useWindowDimensions();

  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const feat = searchParams.get('feat');
  const { profile } = useAppSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getSystemAction());
  }, [dispatch]);

  const queryAgentDataById = ['getAgentDataById', { key }];

  const { isLoading, data: agentData, refetch: reloadAgent } = useQuery(
    queryAgentDataById,
    () => {
      if (key) {
        return getAgentByIdService(key);
      }
      return undefined;
    },
    {
      enabled: !!key,
    }
  );

  useEffect(() => {
    if (!networkStatus.online) {
      if (!isInitialLoad) {
        message.warning('Network error. Please check your connection.');
      }
      setIsInitialLoad(false);
    } else if (!isInitialLoad) {
      message.success('You are online.');
    }
  }, [networkStatus.online, isInitialLoad]);

  /* Queries */
  const { data: overViewData } = useQuery({
    queryKey: ['get-overview'],
    queryFn: getOverViewService,
  });
  /* Ngày hết hạn */
  const expried = dayjs(overViewData?.planData?.endDate).diff(
    new Date(),
    'second'
  );
  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      dispatch(getProfileAction()).unwrap().finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (overViewData?.requireDelInfo?.status === 7 && dayjs(overViewData?.requireDelInfo?.startDate).diff(dayjs()) < 0 && window.location.pathname === ROUTE_PATHS.AGENT) {
      navigate(ROUTE_PATHS.PLAN_INACTIVE, { replace: true });
    }

    if (expried <= 0 && overViewData?.planData?.endDate) {
      if (window.location.pathname !== ROUTE_PATHS.MONITOR_DASHBOARD
        && window.location.pathname !== ROUTE_PATHS.OVER_VIEW
        && window.location.pathname !== ROUTE_PATHS.CHECKOUT
        && window.location.pathname !== ROUTE_PATHS.CHANGE_PLAN
      ) {
        navigate(ROUTE_PATHS.MONITOR_DASHBOARD, { replace: true });
      }
    }
  }, [expried, navigate, overViewData?.planData?.endDate, overViewData?.requireDelInfo?.startDate, overViewData?.requireDelInfo?.status]);

  useEffect(() => {
    if (width <= COLLAPSE_BREAKPOINT) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [width]);

  const valueContext = useMemo(
    () => ({
      getOverview: () => getOverViewService(),
      planData: {
        code: overViewData?.planData?.code || '',
        startDate: overViewData?.planData?.startDate || '',
        endDate: overViewData?.planData?.endDate || '',
        billingType: overViewData?.planData?.billingType ?? 0,
        status: overViewData?.planData?.status ?? 0,
        orderCode: overViewData?.planData?.orderCode || '',
        packageQty: overViewData?.planData?.packageQty ?? 1,
        packageData: {
          name: overViewData?.planData?.packageData?.name || '',
          duration: overViewData?.planData?.packageData?.duration ?? 0,
          price: overViewData?.planData?.packageData?.price ?? 0,
          id: overViewData?.planData?.packageData?.id ?? 0,
        },
        addOnData: overViewData?.planData?.addOnData?.map((addOn) => ({
          servicePackageAddOnData: {
            name: addOn.servicePackageAddOnData?.name || '',
            duration: addOn?.servicePackageAddOnData?.duration ?? 0,
          },
          quantity: addOn?.quantity ?? 0,
          price: addOn?.price ?? 0,
          totalPrice: addOn?.totalPrice ?? 0,
          endAt: addOn?.endAt,
          startAt: addOn?.startAt,
          status: addOn?.status
        })) || [],
        createdAt: overViewData?.planData?.createdAt || '',
        updatedAt: overViewData?.planData?.updatedAt || '',
        isTrial: overViewData?.planData?.isTrial ?? false
      },
      invoiceData: {
        invoiceType: overViewData?.invoiceData?.invoiceType ?? 0,
        taxCode: overViewData?.invoiceData?.taxCode || '',
        companyName: overViewData?.invoiceData?.companyName || '',
        fullAddress: overViewData?.invoiceData?.fullAddress || '',
        invoiceEmails: overViewData?.invoiceData?.invoiceEmails?.map((email: string) => email) || [],
        nationalId: overViewData?.invoiceData?.nationalId || '',
        fullName: overViewData?.invoiceData?.fullName || '',
      },
      requireDelInfo: {
        code: '',
        reqDelData: {
          agentCodes: [],
          documentCodes: []
        },
        documentData: [],
        endDate: '',
        packageData: {
          name: '',
          code: '',
          duration: 0,
          numberOfAgent: 0,
          numberOfFile: 0,
          totalSizeLimit: 0,
          numberOfWebsite: 0,
          numberOfSession: 0
        },
        startDate: '',
        status: 0
      }
    }),
    [overViewData]
  );

  if (loading) return <Loading variant="fullscreen" />;

  const renderMenuItem = () => {
    const isExpiredPlan = expried <= 0 && !!overViewData?.planData?.endDate;

    const isInactivePlan = overViewData?.requireDelInfo?.status === 7
      && dayjs(overViewData?.requireDelInfo?.startDate).diff(dayjs()) < 0;

    if (isExpiredPlan) {
      return [{ items: menuSidebarExpried }] as MenuItems[];
    }
    if (isInactivePlan) {
      return [{ items: menuSidebarInactive }] as MenuItems[];
    }

    const dataList = menuInsideChatBot(key, profile, feat as string);

    if (dataList.length > 0 && !!key) {
      return dataList;
    }

    return [{ items: menuSidebar }] as MenuItems[];
  };

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  if (isLoading) return <Loading variant="fullscreen" />;

  if (!profile?.customerData.onboardingFlow) {
    return <Navigate to={ROUTE_PATHS.ONBOARDING} />;
  }

  return (
    <BillingContex.Provider value={valueContext}>
      <div className="t-mainLayout">
        <div className="t-mainLayout_header">
          <Header agentData={agentData} reloadAgent={reloadAgent} />
        </div>
        <div className="t-mainLayout_body">
          <div className={mapModifiers('t-mainLayout_left', collapsed && 'collapsed')}>
            <div className={mapModifiers('t-mainLayout_sidebar', collapsed && 'collapsed')}>
              <Sidebar collapsed={collapsed} handleCollapsed={handleToggleCollapse} menuItems={renderMenuItem()} />
            </div>
          </div>
          <div className="t-mainLayout_right">
            <div className="t-mainLayout_content">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </BillingContex.Provider>

  );
};

MainLayout.defaultProps = {
  children: undefined,
};

export default MainLayout;
